import { useState, useCallback } from 'react'
import { Button } from '../../Common/Button'
import { AppDialog } from '../../Common/AppDialog'
import { useNls } from '../../../nls/nlsProvider'
import {
  repairOpsPlaceReadAccess,
  type RepairPlaceAccessRow,
} from '../../../services/ownerConsoleService'

export function RepairPlaceAccessCard() {
  const { t } = useNls()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [results, setResults] = useState<RepairPlaceAccessRow[] | null>(null)
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)

  const handleCheck = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const rows = await repairOpsPlaceReadAccess({ dryRun: true })
      setResults(rows)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }, [])

  const handleApplyRepair = useCallback(async () => {
    setIsConfirmOpen(false)
    setLoading(true)
    setError(null)
    try {
      const rows = await repairOpsPlaceReadAccess({ dryRun: false })
      setResults(rows)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }, [])

  const totalMissing = (results ?? []).reduce((acc, row) => acc + row.missing, 0)
  const canRepair = results !== null && totalMissing > 0 && !loading

  return (
    <article className="ownerCard ownerOpsRepairCard" aria-labelledby="repair-place-access-heading">
      <div className="ownerCardHeader">
        <div>
          <h3 id="repair-place-access-heading">{t('NLSOwnerConsoleRepairPlaceAccessTitle')}</h3>
          <p>{t('NLSOwnerConsoleRepairPlaceAccessDesc')}</p>
        </div>
        <div className="ownerCardActions">
          <Button
            variant="secondary"
            onClick={handleCheck}
            disabled={loading}
          >
            {t('NLSOwnerConsoleRepairCheckBtn')}
          </Button>
          <Button
            variant="primary"
            onClick={() => setIsConfirmOpen(true)}
            disabled={!canRepair}
          >
            {t('NLSOwnerConsoleRepairApplyBtn')}
          </Button>
        </div>
      </div>

      {error ? (
        <p className="ownerError" role="alert">{error}</p>
      ) : null}

      {results !== null ? (
        results.length === 0 ? (
          <p className="ownerEmptyNote">{t('NLSOwnerConsoleRepairNoPlaces')}</p>
        ) : (
          <div className="ownerTableWrap">
            <table className="ownerTable">
              <thead>
                <tr>
                  <th scope="col">{t('NLSOwnerConsoleRepairWsCol')}</th>
                  <th scope="col">{t('NLSOwnerConsoleRepairScannedCol')}</th>
                  <th scope="col">{t('NLSOwnerConsoleRepairMissingCol')}</th>
                  <th scope="col">{t('NLSOwnerConsoleRepairUpdatedCol')}</th>
                </tr>
              </thead>
              <tbody>
                {results.map((row) => (
                  <tr key={row.workspaceId}>
                    <td>{row.name}</td>
                    <td>{row.scanned}</td>
                    <td>{row.missing}</td>
                    <td>{row.updated}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}

      {isConfirmOpen ? (
        <AppDialog
          open={isConfirmOpen}
          onDismiss={() => setIsConfirmOpen(false)}
          title={t('NLSOwnerConsoleRepairConfirmTitle')}
          message={<p>{t('NLSOwnerConsoleRepairConfirmDesc')}</p>}
          actions={[
            {
              label: t('NLSOwnerConsoleRepairCancelBtn'),
              variant: 'ghost',
              onClick: () => setIsConfirmOpen(false),
            },
            {
              label: t('NLSOwnerConsoleRepairConfirmBtn'),
              variant: 'primary',
              onClick: handleApplyRepair,
            },
          ]}
        />
      ) : null}
    </article>
  )
}
